
import { Recipe, Ingredient } from '@/types';
import { toast } from 'sonner';

export const parseRecipesCsv = (csvText: string): Omit<Recipe, 'id' | 'createdAt' | 'updatedAt'>[] => {
  // Split by lines, ignore empty lines, and skip header row
  const lines = csvText.split('\n').filter(line => line.trim()).slice(1);
  
  console.log(`Processing ${lines.length} lines from CSV file`);
  
  const recipes: Omit<Recipe, 'id' | 'createdAt' | 'updatedAt'>[] = [];
  const errors: string[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    try {
      const line = lines[i];
      // Use a more robust CSV parsing approach
      const columns = parseCSVLine(line);
      
      if (columns.length < 5) {
        errors.push(`Fila ${i + 2}: Número insuficiente de columnas (${columns.length})`);
        continue;
      }
      
      // Extraemos los valores básicos
      const [
        title,
        description, 
        imageUrl,
        prepTimeStr,
        cookTimeStr,
        servingsStr,
        caloriesStr,
        proteinStr,
        carbsStr,
        fatStr,
        ironStr,
        calciumStr,
        zincStr,
        magnesiumStr,
        omega3Str,
        nutriscoreStr,
        ingredientsText,
        instructionsText,
        tagsText
      ] = columns;
      
      // Valores numéricos
      const prepTime = parseInt(prepTimeStr, 10) || 0;
      const cookTime = parseInt(cookTimeStr, 10) || 0;
      const servings = parseInt(servingsStr, 10) || 1;
      const calories = parseInt(caloriesStr, 10) || 0;
      const protein = parseFloat(proteinStr) || 0;
      const carbs = parseFloat(carbsStr) || 0;
      const fat = parseFloat(fatStr) || 0;
      const iron = parseFloat(ironStr) || 0;
      const calcium = parseFloat(calciumStr) || 0;
      const zinc = parseFloat(zincStr) || 0;
      const magnesium = parseFloat(magnesiumStr) || 0;
      const omega3 = parseFloat(omega3Str) || 0;
      const nutriscore = parseInt(nutriscoreStr, 10) || 50;

      // Process ingredients using multiple strategies
      let ingredients: Ingredient[] = [];
      
      if (ingredientsText) {
        ingredients = parseIngredients(ingredientsText);
        console.log(`Parsed ${ingredients.length} ingredients using new method`);
      }
      
      // If no ingredients were found, create a default one
      if (ingredients.length === 0) {
        ingredients = [
          { id: `ing-default-${Date.now()}`, name: 'Ingrediente 1', amount: 1, unit: 'unidad' }
        ];
      }
      
      // Process instructions using multiple strategies
      let instructions: string[] = [];
      
      if (instructionsText) {
        instructions = parseInstructions(instructionsText);
        console.log(`Parsed ${instructions.length} instructions using new method`);
      }
      
      // If no instructions were found, create a default one
      if (instructions.length === 0) {
        instructions = ['Paso 1 de la receta'];
      }
      
      // Process tags
      let tags: string[] = [];
      
      if (tagsText) {
        tags = parseTags(tagsText);
      }
      
      // Debug
      console.log(`Receta procesada: ${title || 'Sin título'}`, {
        ingredientes: ingredients.length,
        instrucciones: instructions.length,
        etiquetas: tags.length
      });
      
      const recipe = {
        title: title || 'Sin título',
        description: description || '',
        imageUrl: imageUrl || 'https://images.unsplash.com/photo-1618160702438-9b02ab6515c9',
        prepTime,
        cookTime,
        servings,
        calories,
        protein,
        carbs,
        fat,
        iron,
        calcium,
        zinc,
        magnesium,
        omega3,
        nutriscore,
        ingredients,
        instructions,
        tags,
      };
      
      recipes.push(recipe);
    } catch (error) {
      console.error(`Error parsing CSV row ${i + 2}:`, error);
      errors.push(`Fila ${i + 2}: Error general de procesamiento`);
    }
  }
  
  if (errors.length > 0) {
    console.warn(`Found ${errors.length} errors while parsing CSV file.`);
    if (errors.length <= 3) {
      errors.forEach(err => toast.error(err));
    } else {
      toast.error(`Se encontraron ${errors.length} errores al procesar el archivo CSV. Revisa la consola para más detalles.`);
    }
  }
  
  console.log(`Successfully parsed ${recipes.length} recipes from CSV file`);
  return recipes;
};

// Improved CSV line parser that handles quoted fields with embedded commas and newlines
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let currentField = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      // Check for escaped quotes (two double quotes in a row)
      if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
        currentField += '"';
        i++; // Skip the next quote as it's part of the escaped sequence
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // Field delimiter found outside of quotes
      result.push(currentField);
      currentField = '';
    } else {
      // Regular character
      currentField += char;
    }
  }
  
  // Add the last field
  result.push(currentField);
  
  return result.map(field => field.trim());
}

// Function to parse ingredients text in multiple formats
function parseIngredients(text: string): Ingredient[] {
  const ingredients: Ingredient[] = [];
  console.log('Parsing ingredients:', text.substring(0, 100) + (text.length > 100 ? '...' : ''));
  
  // First, try to detect if it's JSON
  if (text.trim().startsWith('[') || text.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        console.log('Parsing ingredients as JSON array');
        return parsed.map((ing, idx) => ({
          id: ing.id || `ing-${Date.now()}-${idx}`,
          name: ing.name || `Ingrediente ${idx + 1}`,
          amount: typeof ing.amount === 'number' ? ing.amount : parseFloat(String(ing.amount)) || 1,
          unit: ing.unit || 'unidad'
        }));
      }
    } catch (e) {
      console.log('Not valid JSON, continuing with other parsing methods');
    }
  }
  
  // Check for "Ingredientes" header
  if (text.includes('Ingredientes')) {
    console.log('Found "Ingredientes" header');
    const lines = text.split('\n');
    let capturingIngredients = false;
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      
      if (trimmedLine === 'Ingredientes') {
        capturingIngredients = true;
        continue;
      }
      
      // Stop capturing if we hit the Instructions section
      if (trimmedLine === 'Instrucciones') {
        break;
      }
      
      if (capturingIngredients && trimmedLine) {
        // Try to extract amount, unit, and name
        const parts = trimmedLine.match(/^([\d.]+)\s+(\w+)\s+(.+)$/);
        
        if (parts) {
          ingredients.push({
            id: `ing-${Date.now()}-${ingredients.length}`,
            name: parts[3],
            amount: parseFloat(parts[1]),
            unit: parts[2]
          });
        } else {
          // If format doesn't match, just use the whole line as the name
          ingredients.push({
            id: `ing-${Date.now()}-${ingredients.length}`,
            name: trimmedLine,
            amount: 1,
            unit: 'unidad'
          });
        }
      }
    }
    
    if (ingredients.length > 0) {
      return ingredients;
    }
  }
  
  // If the above methods fail, try a simple line or comma split
  const items = text.includes('\n') 
    ? text.split('\n') 
    : text.split(',');
  
  return items
    .filter(item => item.trim())
    .map((item, idx) => {
      const trimmed = item.trim();
      
      // Try to parse amount, unit, and name
      const match = trimmed.match(/^([\d.\/]+)\s*(\w+)?\s*(.+)?$/);
      
      if (match) {
        const [_, amountStr, unit, name] = match;
        let amount = parseFloat(amountStr);
        
        // Handle fractions
        if (isNaN(amount) && amountStr.includes('/')) {
          const [num, den] = amountStr.split('/');
          amount = parseInt(num, 10) / parseInt(den, 10);
        }
        
        return {
          id: `ing-${Date.now()}-${idx}`,
          name: name ? name.trim() : trimmed,
          amount: isNaN(amount) ? 1 : amount,
          unit: unit ? unit.trim() : 'unidad'
        };
      }
      
      return {
        id: `ing-${Date.now()}-${idx}`,
        name: trimmed,
        amount: 1,
        unit: 'unidad'
      };
    });
}

// Function to parse instructions text in multiple formats
function parseInstructions(text: string): string[] {
  console.log('Parsing instructions:', text.substring(0, 100) + (text.length > 100 ? '...' : ''));
  
  // Try to parse as JSON if it looks like an array
  if (text.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        console.log('Parsing instructions as JSON array');
        return parsed.map(String);
      }
    } catch (e) {
      console.log('Not valid JSON, continuing with other parsing methods');
    }
  }
  
  // Check for "Instrucciones" header
  if (text.includes('Instrucciones')) {
    console.log('Found "Instrucciones" header');
    const lines = text.split('\n');
    const instructions: string[] = [];
    let capturingInstructions = false;
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      
      if (trimmedLine === 'Instrucciones') {
        capturingInstructions = true;
        continue;
      }
      
      if (capturingInstructions && trimmedLine) {
        // Remove step numbers if present (e.g., "1. " or "1) ")
        instructions.push(trimmedLine.replace(/^\d+[.)]\s*/, ''));
      }
    }
    
    if (instructions.length > 0) {
      return instructions;
    }
  }
  
  // Try different splitting strategies
  if (text.includes('\n')) {
    // Split by newlines
    return text
      .split('\n')
      .filter(line => line.trim())
      .map(line => line.replace(/^\d+[.)]\s*/, '').trim());
  } else if (text.match(/\d+[.)]\s+/)) {
    // Split by numbered steps pattern
    return text
      .split(/\d+[.)]\s+/)
      .filter(step => step.trim())
      .map(step => step.trim());
  } else if (text.includes('.')) {
    // Split by periods
    return text
      .split('.')
      .filter(step => step.trim())
      .map(step => step.trim());
  }
  
  // If all else fails, treat the entire text as one instruction
  return [text.trim()];
}

// Function to parse tags
function parseTags(text: string): string[] {
  if (!text.trim()) return [];
  
  // Try to parse as JSON if it looks like an array
  if (text.trim().startsWith('[')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map(String);
      }
    } catch (e) {
      // Ignore error and continue
    }
  }
  
  // Split by comma or newline
  return text
    .split(/[,\n]/)
    .map(tag => tag.trim())
    .filter(Boolean);
}

// Generamos una plantilla CSV más simple y amigable
export const generateRecipesCsvTemplate = (): string => {
  const instructions = `INSTRUCCIONES PARA IMPORTAR RECETAS:
1. No modifiques la primera fila (encabezados).
2. Cada fila debajo de los encabezados representa una receta.
3. FORMATO ULTRA SIMPLE: Ya no es necesario formatear como JSON.

FORMATO PARA INGREDIENTES:
- Simplemente escribe la palabra "Ingredientes" seguida de una lista con cada ingrediente en una línea nueva.
- Ejemplo:
  Ingredientes
  200g Tofu
  1 Zanahoria
  1/2 Cebolla

FORMATO PARA INSTRUCCIONES:
- Simplemente escribe la palabra "Instrucciones" seguida de una lista numerada con cada paso.
- Ejemplo:
  Instrucciones
  1. Cortar el tofu en cubos.
  2. Pelar la zanahoria.
  3. Mezclar todo.

FORMATO PARA ETIQUETAS:
- Separadas por comas.
- Ejemplo: "vegano, proteína, rápido"

CONSEJOS PARA EVITAR ERRORES:
- Guarda el CSV con codificación UTF-8
- Si tienes problemas, intenta importar menos recetas a la vez (1-2 recetas por archivo)

UNIDADES PARA CAMPOS NUTRICIONALES:
- Calorías: kcal
- Proteínas, Carbohidratos, Grasas: gramos (g)
- Hierro, Calcio, Zinc, Magnesio: miligramos (mg)
- Omega 3: gramos (g)
- Nutriscore: valor de 0 a 100`;

  // Definir los encabezados
  const headers = [
    'title',
    'description',
    'imageUrl',
    'prepTime',
    'cookTime',
    'servings',
    'calories',
    'protein',
    'carbs',
    'fat',
    'iron',
    'calcium',
    'zinc',
    'magnesium',
    'omega3',
    'nutriscore',
    'ingredients',
    'instructions',
    'tags'
  ].join(',');
  
  // Ejemplos con formato ultra simple
  const sampleRow = [
    'Ensalada de Tofu',
    'Una deliciosa ensalada vegana con tofu y vegetales frescos',
    'https://images.unsplash.com/photo-1618160702438-9b02ab6515c9',
    '15',  // prepTime
    '20',  // cookTime
    '2',   // servings
    '320', // calories
    '12',  // protein
    '45',  // carbs
    '9',   // fat
    '8',   // iron (mg)
    '200', // calcium (mg)
    '3',   // zinc (mg)
    '100', // magnesium (mg)
    '2.5', // omega3 (g)
    '75',  // nutriscore
    'Ingredientes\n200g Tofu\n1 Zanahoria\n1 Aguacate\n1 Limón\n1 manojo Cilantro',  // ingredientes en formato simple
    'Instrucciones\n1. Lavar la quinoa y cocinarla.\n2. Cortar el tofu en cubos.\n3. Mezclar todos los ingredientes.',  // instrucciones en formato simple
    'proteína, sin gluten, rápido'  // etiquetas en texto plano
  ].join(',');
  
  // Retornar plantilla con instrucciones y fila de ejemplo
  return `${instructions}\n\n${headers}\n${sampleRow}`;
};

export const exportRecipesToCsv = (recipes: Recipe[]): string => {
  // Headers
  const headers = [
    'title',
    'description',
    'imageUrl',
    'prepTime',
    'cookTime',
    'servings',
    'calories',
    'protein',
    'carbs',
    'fat',
    'iron',
    'calcium',
    'zinc',
    'magnesium',
    'omega3',
    'nutriscore',
    'ingredients',
    'instructions',
    'tags'
  ].join(',');

  // Convert recipes to CSV rows
  const rows = recipes.map(recipe => {
    // Format ingredients as text
    const ingredientsText = "Ingredientes\n" + recipe.ingredients
      .map(ing => `${ing.amount} ${ing.unit} ${ing.name}`)
      .join('\n');

    // Format instructions as numbered list
    const instructionsText = "Instrucciones\n" + recipe.instructions
      .map((inst, idx) => `${idx + 1}. ${inst}`)
      .join('\n');

    // Convert recipe to CSV values
    return [
      recipe.title,
      recipe.description || '',
      recipe.imageUrl || '',
      recipe.prepTime.toString() || '',
      recipe.cookTime.toString() || '',
      recipe.servings.toString() || '',
      recipe.calories.toString() || '',
      recipe.protein.toString() || '',
      recipe.carbs.toString() || '',
      recipe.fat.toString() || '',
      recipe.iron.toString() || '',
      recipe.calcium.toString() || '',
      recipe.zinc.toString() || '',
      recipe.magnesium.toString() || '',
      recipe.omega3.toString() || '',
      recipe.nutriscore.toString() || '',
      ingredientsText,
      instructionsText,
      recipe.tags.join(', ')
    ]
    .map(value => `"${String(value).replace(/"/g, '""')}"`) // Escape quotes and wrap in quotes
    .join(',');
  });

  return [headers, ...rows].join('\n');
};
